import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import State from "../../../../../../context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, theme, Progress, Flex } from "antd";
import BreadcrumbProposalStep from "../../../../components/BreadcrumbProposalStep";
import CardSkeleton from "../../../../../../atom/CardSkeleton";
import ModalAdvancedSettings from "../ModalAdvancedSettings";
import ModalConfirmSaveProposal from "../ModalConfirmSaveProposal";
import ModalGoalSettings from "../../../../../goals/components/ModalGoalSettings";
import ModalLevelProductDetails from "../../../../components/ModalLevelProductDetails";
import ModalLevelRelatedProducts from "../../../../components/ModalLevelRelatedProducts";
import SectionGoalDetails from "./component/SectionGoalDetails";
import SectionReviewProposal from "./component/SectionReviewProposal";

import StyledContainer from "./style";

import {
  DEFAULT_ADVISOR_FEE,
  DEFAULT_BIRTH_YEAR,
  DEFAULT_RETIREMENT_AGE,
  DEFAULT_WITHDRAWAL_START_YEAR,
  DEFAULT_WITHDRAWAL_LEVEL,
} from "../../../../../../utils/constant";
import { PROPOSAL_DEFAULT_DATA_LEVEL } from "../../../../constants";

import {
  convertCurrentAgeRange,
  convertInvestmentDurationRange,
  getLastSavedProposalProps,
  getManuallyUpdatedOAGoalRecommendedProduct,
  isGrowthTypeRiskTolerance,
  isLockedByAdminView,
} from "../../../../../../utils/helper/specialized";
import { getRecommendedProductId } from "./helpers";
import { addGoal, updateGoal } from "../../../../request";

import { faClose } from "@fortawesome/free-solid-svg-icons";

const ViewOneGoal = ({ handleDownloadProposal }) => {
  const { token } = theme.useToken();
  const [state] = useContext(State);
  const [confirmSaveDialogOpened, setConfirmSaveDialogOpened] = useState(false);
  const [proposalData, setProposalData] = useState(null);
  const [proposalDirty, setProposalDirty] = useState(false);
  const [isCompareChartDirty, setIsCompareChartDirty] = useState(false);

  const prospectObjective = state.getPreferenceValue("prospectObjective");
  const productMap = state.getPreferenceValue("productMap");
  const guideInitialData = state.getPreferenceValue("guideInitialData");

  const activeGoal = state.activeGoal;
  const productsList = state.productsList;

  useEffect(() => {
    if (!state.loading && state._id) {
      if (!proposalData) {
        if (activeGoal && productMap) {
          const activeGoalLastSavedProps =
            productMap[activeGoal][productMap[activeGoal].length - 1];

          setProposalData({
            ...activeGoalLastSavedProps,
            birthYear:
              guideInitialData?.yearBorn || activeGoalLastSavedProps.birthYear,
            contributions:
              activeGoalLastSavedProps.contributions ??
              activeGoalLastSavedProps.savingsEachMonth * 12,
            lengthOfInvestment: undefined,
            retirementAge:
              guideInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
          });
        } else if (productMap) {
          const lastSavedProposalProps = getLastSavedProposalProps(productMap);
          const goalInitialData = prospectObjective ?? guideInitialData;

          setProposalData({
            ...lastSavedProposalProps,
            ...goalInitialData,
            birthYear:
              guideInitialData?.yearBorn ||
              goalInitialData?.birthYear ||
              DEFAULT_BIRTH_YEAR,
            lengthOfInvestment: undefined,
            retirementAge:
              goalInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
            timestamp: undefined,
          });
        } else if (prospectObjective) {
          const investmentDuration = isNaN(prospectObjective.investmentDuration)
            ? convertInvestmentDurationRange(
                prospectObjective.investmentDuration
              )
            : +prospectObjective.investmentDuration;

          const currentAge =
            prospectObjective.currentAge ??
            convertCurrentAgeRange(prospectObjective.currentAgeRange);

          setProposalData({
            ...prospectObjective,
            advisorFee: prospectObjective.advisorFee ?? DEFAULT_ADVISOR_FEE,
            birthYear:
              guideInitialData?.yearBorn ||
              prospectObjective.birthYear ||
              DEFAULT_BIRTH_YEAR,
            currentAge,
            incomeChartType: isGrowthTypeRiskTolerance(
              prospectObjective.investmentObjective
            )
              ? "contributions"
              : "income",
            investmentDuration,
            lengthOfInvestment: undefined,
            retirementAge:
              guideInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
            withdrawalLevel:
              prospectObjective.withdrawalLevel ?? DEFAULT_WITHDRAWAL_LEVEL,
            yearToStartWithdrawals:
              prospectObjective.yearToStartWithdrawals ||
              Math.round(investmentDuration / 2) ||
              DEFAULT_WITHDRAWAL_START_YEAR,
          });
        } else {
          // onboarding quiz skipped
          setProposalData(PROPOSAL_DEFAULT_DATA_LEVEL);
        }
      }
    }
  }, [state]);

  useEffect(() => {
    if (state.selectedProduct) return;

    if (proposalData) {
      const recommendedProductId =
        state.selectedProduct ??
        proposalData.recommendedProductId ??
        getRecommendedProductId({
          organization: state.organization?.name ?? state.orgName,
          productsList,
          proposalData,
        });

      if (recommendedProductId) {
        state.setKeyValue("selectedProduct", recommendedProductId);
        state.setKeyValue("compareProducts", [recommendedProductId]);
      }
    }
  }, [productsList, proposalData]);

  useEffect(() => {
    if (!activeGoal) {
      setProposalDirty(true);
    }
  }, [activeGoal]);

  const getBreadcrumbActiveStep = () => {
    if (state.activeSection === "reviewProposal") return "review";

    return "select";
  };

  const getProgressPercent = () => {
    if (state.activeSection === "reviewProposal") return 100;

    return 100 / 2;
  };

  const handleCardClick = cardId => {
    state.setKeyValue("productDetailsId", cardId);
    state.setKeyValue("openModalProductDetails", true);
  };

  const handleProposalDataChange = useDebouncedCallback((values, skipDirty) => {
    if (!isLockedByAdminView(state.showWarning)) {
      !skipDirty && setProposalDirty(true);

      setProposalData(lastState => ({
        ...lastState,
        ...values,
      }));
    }
  }, 1000);

  const handleSaveProposal = () => {
    state.setKeyValue("loading", true);

    const request = activeGoal ? updateGoal : addGoal;

    request({
      email: state._id,
      goalId: activeGoal,
      proposalData: {
        ...proposalData,
        investmentDuration:
          proposalData?.investmentDuration ?? proposalData?.lengthOfInvestment,
        organization: state.organization?.name?.toLowerCase(),
        productId: state.selectedProduct,
      },
    })
      .then(() => {
        setProposalDirty(false);
        state.showSuccess("Proposal saved");
        setConfirmSaveDialogOpened(false);
        setTimeout(() => {
          state.setUserData().then(data => {
            if (activeGoal) {
              //update existed active goal
              if (data.preferences?.valueMap?.productMap) {
                setProposalData(
                  data.preferences.valueMap.productMap[activeGoal][
                    data.preferences.valueMap.productMap[activeGoal].length - 1
                  ]
                );
              }
            } else {
              //set new created goal ID as active goal
              const createdGoalId = Object.keys(
                data.preferences.valueMap.productMap
              )[Object.keys(data.preferences.valueMap.productMap).length - 1];

              state.setKeyValue("activeGoal", createdGoalId);
              setProposalData(
                data.preferences.valueMap.productMap[createdGoalId][
                  data.preferences.valueMap.productMap[createdGoalId].length - 1
                ]
              );
            }
            state.setKeyValue("loading", false);
          });
        }, 1000);
      })
      .catch(error => {
        state.setKeyValue("loading", false);
        state.showError(error);
      });
  };

  const handleUpdateGoal = updatedProposalData => {
    setProposalData(updatedProposalData);
    state.closeModal("goalSettingsModal");
    state.showSuccess("Goal settings updated");
    setProposalDirty(true);

    const recommendedProduct = getManuallyUpdatedOAGoalRecommendedProduct({
      customRiskTolerance: updatedProposalData.riskTolerance,
      currentSelectedProduct: productsList.find(
        it => it._id === state.selectedProduct
      ),
      productsList,
    });

    if (recommendedProduct) {
      state.setKeyValue("selectedProduct", recommendedProduct._id);
      state.setKeyValue("compareProducts", [recommendedProduct._id]);
    }
  };

  const handleGoalClose = () => {
    state.setKeyValue("activeGoal", undefined);
    state.setKeyValue("viewMode", "goals");
    state.setKeyValue("activeSection", "goalDetails");
    state.setKeyValue("showStressTesting", false);
    state.setKeyValue("showCompareMode", false);
    state.setKeyValue("compareProducts", [state.selectedProduct]);
  };

  const handleSelectFinancialProduct = productData => {
    if (!productData) return;

    state.setKeyValue("openModalRelatedProducts", false);

    handleProposalDataChange({
      ...proposalData,
      riskTolerance: productData.riskTolerance,
    });

    state.setKeyValue("selectedProduct", productData._id);
    state.setKeyValue("compareProducts", [productData._id]);
  };

  const handleAddToCompare = productData => {
    if (!productData) return;

    if (state.compareProducts?.includes(productData._id)) {
      state.showWarning("Product already added to the Compare.");
      return;
    }

    state.setKeyValue("openModalRelatedProducts", false);
    state.setKeyValue("compareProducts", [
      ...state.compareProducts,
      productData._id,
    ]);

    setIsCompareChartDirty(true);
  };

  const submitAdvancedSettings = newValues => {
    setProposalData(lastState => ({
      ...lastState,
      ...newValues,
    }));
    state.setKeyValue("openModalAdvancedSettings", false);
    setProposalDirty(true);
  };

  return (
    <StyledContainer token={token}>
      <header>
        <h4>
          Proposal:{" "}
          <b>
            {state.personalInfo?.firstName}{" "}
            {guideInitialData?.spouseFirstName &&
              `& ${guideInitialData.spouseFirstName}`}{" "}
            {state.personalInfo?.lastName}
          </b>
        </h4>

        <BreadcrumbProposalStep activeStep={getBreadcrumbActiveStep()} />

        <Flex gap={16}>
          {!proposalDirty && proposalData?.timestamp ? (
            <Button
              disabled={state.activeSection === "reviewProposal"}
              onClick={() =>
                state.setKeyValue("activeSection", "reviewProposal")
              }
              shape="round"
              type="primary"
            >
              Review Proposal
            </Button>
          ) : (
            <Button
              onClick={() => setConfirmSaveDialogOpened(true)}
              type="text"
            >
              Save
            </Button>
          )}

          {
            // allow to close one proposal view
            // only if we have already saved proposals
            activeGoal && (
              <Button
                icon={<FontAwesomeIcon icon={faClose} />}
                onClick={handleGoalClose}
                shape="round"
              />
            )
          }
        </Flex>
      </header>

      <Progress
        percent={getProgressPercent()}
        showInfo={false}
        strokeColor={{ from: "#E2E86C", to: "#4F8083" }}
        strokeLinecap="square"
        style={{ lineHeight: "unset" }}
        trailColor="#A9C8CA"
      />

      <main>
        <CardSkeleton loading={state.loading}>
          {state.activeSection === "reviewProposal" ? (
            <SectionReviewProposal
              handleClose={() =>
                state.setKeyValue("activeSection", "goalDetails")
              }
              handleDownloadProposal={() =>
                handleDownloadProposal(state.activeGoal)
              }
              productLiveAssessment={state.productLiveAssessment}
              productsLiveSimulation={state.productsLiveSimulation}
              proposalData={proposalData}
              selectedProductData={state.productsList?.find(
                it => it._id === state.selectedProduct
              )}
            />
          ) : (
            <SectionGoalDetails
              handleCardClick={handleCardClick}
              handleProposalDataChange={handleProposalDataChange}
              isCompareChartDirty={isCompareChartDirty}
              productsList={productsList}
              proposalData={proposalData}
              setIsCompareChartDirty={setIsCompareChartDirty}
            />
          )}
        </CardSkeleton>
      </main>

      <ModalAdvancedSettings
        handleClose={() =>
          state.setKeyValue("openModalAdvancedSettings", false)
        }
        loading={state.loading}
        onFinish={submitAdvancedSettings}
        open={state.openModalAdvancedSettings}
        proposalData={proposalData}
      />
      <ModalConfirmSaveProposal
        handleClose={() => setConfirmSaveDialogOpened(false)}
        handleSaveProposal={handleSaveProposal}
        isFirstSave={!(productMap ?? [])[activeGoal]}
        open={confirmSaveDialogOpened}
      />
      <ModalGoalSettings
        handleClose={() => state.closeModal("goalSettingsModal")}
        handleUpdateGoal={handleUpdateGoal}
        loading={state.loading}
        open={state.goalSettingsModal}
        organization={state.organization?.name}
        proposalData={proposalData}
      />
      <ModalLevelProductDetails
        handleClose={() => state.setKeyValue("openModalProductDetails", false)}
        investmentAssumptions={proposalData}
        open={state.openModalProductDetails}
        product={state.productsList?.find(
          it => it._id === state.productDetailsId
        )}
        showError={state.showError}
      />
      <ModalLevelRelatedProducts
        handleClose={() => state.setKeyValue("openModalRelatedProducts", false)}
        handleSelectFinancialProduct={
          state.showCompareMode
            ? handleAddToCompare
            : handleSelectFinancialProduct
        }
        open={state.openModalRelatedProducts}
        proposalData={proposalData}
      />
    </StyledContainer>
  );
};

export default ViewOneGoal;
